import {
  ListCommitmentsRequest,
  useRelationshipsRelationshipListCommitmentsQuery,
  useRelationshipsRelationshipShowQuery,
} from "@/services/backend/relationships/api/service";
import { Commitment } from "@/services/backend/relationships/types/relationship";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import t from "@/lang/lang";
import { TableLoadingRow } from "@/shared/components/tables/table-loading-row";
import { TableRtkErrorRow } from "@/shared/components/tables/table-rtk-error-row";
import { TableEmptyRow } from "@/shared/components/tables/table-empty-row";
import { PaginationLimitOffset } from "@/shared/components/tables/pagination-limit-offset";
import {
  AllCommitmentsCheckbox,
  CommitmentCheckbox,
  CommitmentStatusCell,
} from "@/services/backend/relationships/components/commitments-table";
import { weekRFC3339 } from "@/shared/lib/utilities/date";
import { BranchName } from "@/services/backend/company/components/branch-name";
import { ServiceObjectLink } from "@/routes/_components/links/service-object-link";
import { CommitmentSheet } from "@/routes/commitments/_components/commitment-sheet";
import { CustomerName } from "@/services/backend/customers/components/customer-name";
import { ServiceObjectAddress } from "@/services/backend/customers/components/service-object-address";
import { CommentCell } from "@/routes/commitments/_components/comment-cell";
import { ActualHoursCell } from "@/routes/commitments/_components/actual-hours-cell";
import { FulfillmentCell } from "@/services/backend/relationships/components/fulfillment-cell";
import { NavLink } from "react-router";
import { formatEuro } from "@/shared/lib/utilities/format-euro";

export function CommitmentsPlanningTable({
  request,
  onRequestChange,
  selection,
  onSelectionChange,
}: {
  request: ListCommitmentsRequest;
  onRequestChange: (request: ListCommitmentsRequest) => void;
  selection: Commitment[];
  onSelectionChange: (selection: Commitment[]) => void;
}) {
  const {
    data: list,
    isLoading,
    error,
  } = useRelationshipsRelationshipListCommitmentsQuery(request);
  const emptyList = !isLoading && !error && list!.commitments.length === 0;

  return (
    <div className="grid gap-2">
      <div className="overflow-auto rounded-xl border shadow-sm">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10 text-center">
                <AllCommitmentsCheckbox
                  commitments={list?.commitments}
                  selection={selection}
                  onSelectionChange={onSelectionChange}
                />
              </TableHead>
              <TableHead>{t("Status")}</TableHead>
              <TableHead>{t("NL")}</TableHead>
              <TableHead>{t("Kunde")}</TableHead>
              <TableHead>{t("Objekt")}</TableHead>
              <TableHead>{t("DL")}</TableHead>
              <TableHead>{t("Turnus")}</TableHead>
              <TableHead>{t("Soll KW")}</TableHead>
              <TableHead>{t("Soll MT")}</TableHead>
              <TableHead>{t("Ist MT")}</TableHead>
              <TableHead>{t("Arbeitsschein(e)")}</TableHead>
              <TableHead>{t("Preis")}</TableHead>
              <TableHead>{t("Kommentar")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && <TableLoadingRow colSpan={colCount} />}
            {error && <TableRtkErrorRow error={error} colSpan={colCount} />}
            {emptyList && <TableEmptyRow colSpan={colCount} />}
            {list?.commitments.map((commitment) => (
              <CommitmentRow
                key={commitment.id}
                commitment={commitment}
                selection={selection}
                onSelectionChange={onSelectionChange}
              />
            ))}
          </TableBody>
        </Table>
      </div>
      {list && !emptyList && (
        <PaginationLimitOffset
          offset={request.offset ?? 0}
          limit={request.limit ?? 0}
          totalCount={list.totalCount}
          setLimitOffset={(limit, offset) => {
            onRequestChange({ ...request, limit, offset });
          }}
          className="ml-auto"
        />
      )}
    </div>
  );
}

const colCount = 11;

function CommitmentRow({
  commitment,
  selection,
  onSelectionChange,
}: {
  commitment: Commitment;
  selection: Commitment[];
  onSelectionChange: (selection: Commitment[]) => void;
}) {
  const { data: relationship, isLoading } =
    useRelationshipsRelationshipShowQuery({
      id: commitment.relationshipId,
    });

  if (isLoading) {
    return <TableLoadingRow colSpan={colCount} />;
  }

  if (!relationship) {
    return <TableEmptyRow colSpan={colCount} />;
  }

  return (
    <TableRow>
      <TableCell className="text-center">
        <CommitmentCheckbox
          commitment={commitment}
          selection={selection}
          onSelectionChange={onSelectionChange}
        />
      </TableCell>
      <TableCell>
        <CommitmentStatusCell commitment={commitment} />
      </TableCell>
      <TableCell>
        <BranchName branchId={relationship.branchId} />
      </TableCell>
      <TableCell>
        <CustomerName customerId={relationship.customerId} />
      </TableCell>
      <TableCell className="space-x-2">
        <ServiceObjectLink id={relationship.serviceObjectId} />
        <ServiceObjectAddress id={relationship.serviceObjectId} />
      </TableCell>
      <TableCell>{commitment.serviceKind}</TableCell>
      <TableCell className="text-center">{commitment.cycle}</TableCell>
      <TableCell className="text-center">
        {weekRFC3339(commitment.date)}
      </TableCell>
      <TableCell className="text-center">
        <CommitmentSheet
          triggerText={`${(commitment.plannedHours / 8.0).toLocaleString("de", { maximumFractionDigits: 2 })} MT`}
          relationship={relationship}
          commitment={commitment}
        />
      </TableCell>
      <TableCell>
        <ActualHoursCell commitment={commitment} />
      </TableCell>
      <TableCell>
        <FulfillmentCell
          relationship={relationship}
          commitment={commitment}
          renderDocumentLink={(path, children) => (
            <NavLink to={path}>{children}</NavLink>
          )}
        />
      </TableCell>
      <TableCell className="text-right">
        {formatEuro(commitment.price)}
      </TableCell>
      <TableCell>
        <CommentCell commitment={commitment} />
      </TableCell>
    </TableRow>
  );
}
